"use client";

import axios from "axios";
import { useRef, useState } from "react";
import { Turnstile } from "@marsidev/react-turnstile";
import { cn } from "@/utils";

const topics = [
  "Pre-sale question",
  "Product support",
  "Custom development",
  "Partnership",
  "Something else",
];

const field =
  "jlt-w-full jlt-rounded-[14px] jlt-border-[1.5px] jlt-border-pl-line2 jlt-bg-pl-bg jlt-px-4 jlt-py-3 jlt-text-[15px] jlt-text-pl-ink jlt-transition-[border-color,box-shadow,background] jlt-duration-150 focus:jlt-border-pl-blue focus:jlt-bg-white focus:jlt-shadow-[0_0_0_4px_#E8EDFF] focus:jlt-outline-none";

const label = "jlt-text-[13.5px] jlt-font-semibold jlt-text-pl-ink";

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ContactForm = () => {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [token, setToken] = useState("");
  const turnstileRef = useRef<{ reset: () => void } | null>(null);

  const siteKey = process.env.NEXT_PUBLIC_CF_TURNSTILE_SITE_KEY;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    const form = e.currentTarget;
    const data = new FormData(form);
    const email = String(data.get("email") || "").trim();

    if (!emailPattern.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setSending(true);

    try {
      const { data: result } = await axios.post("/api/contact", {
        names: {
          first_name: String(data.get("first_name") || "").trim(),
          last_name: String(data.get("last_name") || "").trim(),
        },
        email,
        subject: String(data.get("subject") || ""),
        website: String(data.get("website") || "").trim(),
        message: String(data.get("message") || "").trim(),
        site_source: process.env.NEXT_PUBLIC_SOURCE_SITE,
        ...(token && { turnstile_token: token }),
      });

      if (result?.success) {
        form.reset();
        setSent(true);
      } else {
        setError(result?.message || "Something went wrong. Please try again.");
      }
    } catch {
      setError("We could not reach the server. Please try again in a moment.");
    } finally {
      setSending(false);
      turnstileRef.current?.reset();
      setToken("");
    }
  };

  if (sent) {
    return (
      <div className="jlt-animate-fadeup jlt-px-5 jlt-py-11 jlt-text-center">
        <div className="jlt-mb-3 jlt-text-[58px]">🎉</div>
        <h3 className="jlt-mb-2 jlt-mt-0 jlt-font-display jlt-text-[24px] jlt-font-extrabold jlt-text-pl-ink">
          Message sent!
        </h3>
        <p className="jlt-mb-6 jlt-leading-[1.62] jlt-text-pl-mute">
          Thanks for reaching out — a real human from our team will reply within one business day.
        </p>
        <button
          type="button"
          onClick={() => setSent(false)}
          className="jlt-inline-flex jlt-items-center jlt-justify-center jlt-rounded-full jlt-border-[1.5px] jlt-border-pl-line2 jlt-bg-white jlt-px-[22px] jlt-py-3 jlt-text-[15px] jlt-font-semibold jlt-text-pl-ink jlt-transition-colors hover:jlt-border-pl-ink"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <h3 className="jlt-mb-[6px] jlt-mt-0 jlt-font-display jlt-text-[24px] jlt-font-extrabold jlt-text-pl-ink">
        Send us a message
      </h3>
      <p className="jlt-mb-6 jlt-mt-0 jlt-text-[14.5px] jlt-leading-[1.62] jlt-text-pl-mute">
        We reply within one business day — usually much faster.
      </p>

      <div className="jlt-grid jlt-grid-cols-1 jlt-gap-[14px] sm:jlt-grid-cols-2">
        <div className="jlt-mb-[14px] jlt-flex jlt-flex-col jlt-gap-[7px]">
          <label className={label} htmlFor="first_name">
            First name
          </label>
          <input id="first_name" name="first_name" className={field} placeholder="Jane" required />
        </div>
        <div className="jlt-mb-[14px] jlt-flex jlt-flex-col jlt-gap-[7px]">
          <label className={label} htmlFor="last_name">
            Last name
          </label>
          <input id="last_name" name="last_name" className={field} placeholder="Cooper" required />
        </div>
      </div>

      <div className="jlt-grid jlt-grid-cols-1 jlt-gap-[14px] sm:jlt-grid-cols-2">
        <div className="jlt-mb-[14px] jlt-flex jlt-flex-col jlt-gap-[7px]">
          <label className={label} htmlFor="email">
            Email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            className={field}
            placeholder="jane@company.com"
            required
          />
        </div>
        <div className="jlt-mb-[14px] jlt-flex jlt-flex-col jlt-gap-[7px]">
          <label className={label} htmlFor="website">
            Website (optional)
          </label>
          <input
            id="website"
            name="website"
            type="url"
            className={field}
            placeholder="https://yoursite.com"
          />
        </div>
      </div>

      <div className="jlt-mb-[14px] jlt-flex jlt-flex-col jlt-gap-[7px]">
        <label className={label} htmlFor="subject">
          Topic
        </label>
        <select id="subject" name="subject" className={field} defaultValue={topics[0]}>
          {topics.map((topic) => (
            <option key={topic}>{topic}</option>
          ))}
        </select>
      </div>

      <div className="jlt-mb-[14px] jlt-flex jlt-flex-col jlt-gap-[7px]">
        <label className={label} htmlFor="message">
          How can we help?
        </label>
        <textarea
          id="message"
          name="message"
          required
          placeholder="Tell us about your project or question…"
          className={cn(field, "jlt-min-h-[110px] jlt-resize-y")}
        />
      </div>

      {siteKey ? (
        <div className="jlt-mb-4">
          <Turnstile ref={turnstileRef as never} siteKey={siteKey} onSuccess={setToken} />
        </div>
      ) : null}

      {error ? (
        <p className="jlt-mb-4 jlt-text-[14px] jlt-leading-[1.62] jlt-text-[#D14E12]">{error}</p>
      ) : null}

      <button
        type="submit"
        disabled={sending}
        className="jlt-inline-flex jlt-w-full jlt-items-center jlt-justify-center jlt-gap-[9px] jlt-rounded-full jlt-bg-pl-blue jlt-px-[30px] jlt-py-4 jlt-text-[16px] jlt-font-semibold jlt-text-white jlt-shadow-[0_10px_26px_-10px_rgba(42,76,255,.55)] jlt-transition-transform jlt-duration-200 jlt-ease-spring hover:-jlt-translate-y-[2px] disabled:jlt-cursor-not-allowed disabled:jlt-opacity-60 disabled:hover:jlt-translate-y-0"
      >
        {sending ? "Sending…" : "Send message →"}
      </button>
    </form>
  );
};

export default ContactForm;
